import { useState, useEffect, useMemo, useRef } from "react";
import {
  Typography,
  Button,
  Stack,
  Chip,
  Avatar,
  Card,
  CardContent,
  CardActions,
  Grid,
  Divider,
  Box,
  Tabs,
  Tab,
  Fade,
  Paper,
  IconButton,
  Tooltip as MuiTooltip,
  Select,
  MenuItem,
  Menu,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useConfig } from "../hooks/useConfig";
import {
  AccessTime,
  NotificationsActive,
  CheckCircle,
  DeliveryDining,
  ShoppingBag,
  Restaurant,
  DeleteOutline,
  KeyboardArrowRight,
  InfoOutlined,
  Person,
  Edit,
  Receipt,
  Block,
  Map as MapIcon,
  Navigation,
  LocalAtm,
  Payments,
  CreditCard,
  TakeoutDining,
  Timer as TimerIcon,
  Search,
  Close,
  Print as PrintIcon,
  Phone,
  TableBar,
} from "@mui/icons-material";
import { CancelOrderDialog } from "../components/CancelOrderDialog";
import { useCollection } from "../hooks/useCollection";
import { useAuth } from "../hooks/useAuth";
import { differenceInMinutes } from "date-fns";
import { useParams, useNavigate } from "react-router-dom";
import { Order, OrderStatus } from "../interfaces/Order";
import { Product } from "../interfaces/product";
import { Category } from "./CategoriesPage";
import { Users } from "../interfaces/Users";
import { OtherProduct } from "../interfaces/OtherProduct";
import { useAnalytics } from "../hooks/useAnalytics";
import { signOut } from "firebase/auth";
import Logout from "@mui/icons-material/Logout";
import { db, auth } from "../firebase/config";
import { writeBatch, doc, getDoc, collection, addDoc, deleteDoc } from "firebase/firestore";
import { removeUndefined, getNormalizedProducts } from "../global/utils";
import { useReactToPrint } from "react-to-print";
import { ComandaReceipt } from "../components/ComandaReceipt";



/* =======================
   🎨 Configuración de Estados
 ======================= */

const statusConfig: Record<
  OrderStatus,
  { label: string; color: "warning" | "primary" | "info" | "success" | "error" | "secondary"; icon: any }
> = {
  verificando_pago: { label: "Validando Pago", color: "info", icon: <Receipt fontSize="small" /> },
  pendiente: { label: "Pendiente", color: "warning", icon: <AccessTime fontSize="small" /> },
  enProceso: { label: "En Cocina", color: "primary", icon: <Restaurant fontSize="small" /> },
  enMasa: { label: "En Masa", color: "primary", icon: <Restaurant fontSize="small" /> },
  enPreparacion: { label: "En Prep.", color: "primary", icon: <Restaurant fontSize="small" /> },
  enEmpaque: { label: "En Empaque", color: "info", icon: <ShoppingBag fontSize="small" /> },
  esperando_domiciliario: { label: "Esperando Domiciliario", color: "warning", icon: <DeliveryDining fontSize="small" /> },
  enCamino: { label: "En Camino", color: "info", icon: <DeliveryDining fontSize="small" /> },
  entregado: { label: "Entregado", color: "success", icon: <CheckCircle fontSize="small" /> },
  porCobrar: { label: "Por Cobrar", color: "secondary", icon: <ShoppingBag fontSize="small" /> },
  "listo / caja": { label: "Listo / Caja", color: "secondary", icon: <ShoppingBag fontSize="small" /> },
  completada: { label: "Completada", color: "success", icon: <CheckCircle fontSize="small" /> },
  cancelada: { label: "Cancelada", color: "error", icon: <DeleteOutline fontSize="small" /> },
};

const STAGE_ORDER: Record<string, number> = {
  verificando_pago: 1,
  pendiente: 1,
  enMasa: 2,
  enProceso: 3,
  enPreparacion: 3,
  enEmpaque: 4,
  esperando_domiciliario: 5,
  enCamino: 6,
  entregado: 7,
  completada: 8,
};

/* =======================
   🎵 Alertas Sonoras
 ======================= */



/* =======================
   🚀 Componente Principal
 ======================= */

export const getOrderDate = (o: any) => {
  const d = o.date || o.createdAt;
  if (!d) return new Date();
  return d.toDate ? d.toDate() : new Date(d);
};

export default function OrdersPage() {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const navigate = useNavigate();
  const { userDoc, canCreate, canDelete } = useAuth();
  const userRole = userDoc?.role?.toLowerCase();

  const [cancelPrompt, setCancelPrompt] = useState<{ order: Order; onConfirm: (reason: string) => void } | null>(null);
  const [logoutAnchorEl, setLogoutAnchorEl] = useState<null | HTMLElement>(null);

  const comandaRef = useRef<HTMLDivElement>(null);
  const [comandaOrder, setComandaOrder] = useState<Order | null>(null);

  const handlePrintComanda = useReactToPrint({
    contentRef: comandaRef,
    documentTitle: comandaOrder ? `Comanda_${comandaOrder.supportCode || comandaOrder.id?.slice(-4)}` : "Comanda",
  });

  const triggerComandaPrint = (orderToPrint: Order) => {
    const products = getNormalizedProducts(orderToPrint);
    setComandaOrder({ ...orderToPrint, products });
    setTimeout(() => {
      handlePrintComanda();
    }, 150);
  };


  const handleLogoutClick = (event: React.MouseEvent<HTMLElement>) => {
    setLogoutAnchorEl(event.currentTarget);
  };

  const handleCloseLogoutMenu = () => {
    setLogoutAnchorEl(null);
  };

  const handleLogout = async () => {
    handleCloseLogoutMenu();
    try {
      await signOut(auth);
      localStorage.removeItem("activeUserRole");
      navigate("/login");
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  const [activeRole, setActiveRole] = useState<string>(() => {
    return localStorage.getItem("activeUserRole") || userDoc?.role || "Masa";
  });

  useEffect(() => {
    if (!localStorage.getItem("activeUserRole") && userDoc?.role) {
      setActiveRole(userDoc.role);
    }
  }, [userDoc?.role]);

  const effectiveRole = useMemo(() => activeRole.toLowerCase(), [activeRole]);

  const getTabForRole = (roleStr: string) => {
    const r = roleStr.toLowerCase();
    if (r.includes("masa")) return 2; // Masa va directo a la pestaña de Preparación
    if (r.includes("prep")) return 2;
    if (r.includes("empaque")) return 3;
    if (r.includes("domi")) return 5;
    if (r.includes("caja")) return 4;
    if (r.includes("admin")) return 0;
    return 0;
  };

  // Determinamos las pestañas permitidas dinámicamente según el rol activo seleccionado y permisos del usuario
  const allowedTabs = useMemo(() => {
    if (userDoc?.isAdmin === true || userRole === "admin" || effectiveRole === "admin") {
      return [0, 2, 3, 5, 4, 6];
    }
    if (effectiveRole.includes("masa")) return [2];
    if (effectiveRole.includes("prep")) return [2];
    if (effectiveRole.includes("empaque")) return [3];
    if (effectiveRole.includes("domi")) return [5];
    if (effectiveRole.includes("caja")) return [4];
    return userDoc?.permissions?.allowedOrderTabs?.filter((t: number) => t !== 1) ?? [0, 2, 3, 5, 4, 6];
  }, [userDoc, userRole, effectiveRole]);

  const [tabValue, setTabValue] = useState(() => {
    const defaultTab = getTabForRole(activeRole);
    return allowedTabs.includes(defaultTab) ? defaultTab : (allowedTabs[0] ?? 0);
  });

  const [searchQueries, setSearchQueries] = useState<Record<number, string>>({});
  const searchQuery = searchQueries[tabValue] || "";
  const setSearchQuery = (query: string) => {
    setSearchQueries(prev => ({
      ...prev,
      [tabValue]: query
    }));
  };

  const handleRoleChange = (newRole: string) => {
    setActiveRole(newRole);
    localStorage.setItem("activeUserRole", newRole);
    const targetTab = getTabForRole(newRole);
    setTabValue(targetTab);
  };

  // Asegurar que si el usuario no tiene acceso a la pestaña actual, se mueva a la primera disponible
  useEffect(() => {
    if (allowedTabs.length > 0 && !allowedTabs.includes(tabValue)) {
      setTabValue(allowedTabs[0]);
    }
  }, [allowedTabs, tabValue]);

  const {
    data: orders = [],
    deleteItem,
    updateItem,
  } = useCollection<Order>(restaurantId!, "orders");

  const { data: allProducts = [] } = useCollection<Product>(restaurantId!, "products");
  const { data: categories = [] } = useCollection<Category>(restaurantId!, "category");
  const { data: allFlavors = [] } = useCollection<any>(restaurantId!, "flavors");
  const { data: users = [] } = useCollection<Users>(restaurantId!, "users");
  const { data: completedOrders = [] } = useCollection<Order>(restaurantId!, "orderssuccess");
  const { data: otherProductsList = [] } = useCollection<OtherProduct>(restaurantId!, "otherProducts");
  const analytics = useAnalytics(completedOrders, "day");

  // Mapa: nombre de categoría → tipo ("food" | "drink")
  // Si una categoría no tiene `type` definido, se trata como comida (por defecto seguro)
  const categoryTypeMap = useMemo(() => {
    const map = new Map<string, "food" | "drink">();
    categories.forEach(cat => map.set(cat.name, cat.type ?? "food"));
    return map;
  }, [categories]);

  // Helper: determina si una categoría es bebida usando el mapa
  const isDrink = (categoryName?: string): boolean => {
    if (!categoryName) return false;
    return categoryTypeMap.get(categoryName) === "drink";
  };

  const handleSendToCashier = async (order: Order) => {
    if (!order.id) return;

    // Verificar si la orden YA FUE PAGADA/COBRADA previamente
    const isAlreadyPaid =
      (order as any).isPaid === true ||
      (order as any).paid === true ||
      (order as any).paymentStatus === "paid" ||
      (order as any).paymentStatus === "pagado";

    if (isAlreadyPaid) {
      // Si la orden ya fue cobrada previamente, auto-facturarla directamente enviándola a orderssuccess
      await handleAutoCheckout(order);
      return;
    }

    const now = new Date();
    const prevDate = (order as any).stateUpdatedAt ? new Date((order as any).stateUpdatedAt) : getOrderDate(order);
    const minutesSpent = differenceInMinutes(now, prevDate);

    const stageDurations = {
      ...(order.stageDurations || {}),
      [order.state]: (order.stageDurations?.[order.state] || 0) + minutesSpent
    };

    const stageMetrics = {
      ...(order.stageMetrics || {})
    };

    let userId = userDoc?.uid || userDoc?.id || "unknown";
    let userName = userDoc?.name || userDoc?.displayName || userDoc?.email || "Desconocido";
    let stageRole = activeRole;

    if (order.state === "enPreparacion" && order.preparadorId) {
      userId = order.preparadorId;
      userName = order.preparadorName || userName;
      stageRole = "Preparación";
    } else if (order.state === "enEmpaque") {
      userId = (order as any).empacadorId || userDoc?.uid || userDoc?.id || "unknown";
      userName = (order as any).empacadorName || userDoc?.name || userDoc?.displayName || "Empacador";
      stageRole = "Empaque";
    } else if (order.state === "enCamino" && order.deliveryDriverId) {
      userId = order.deliveryDriverId;
      userName = order.deliveryDriverName || userName;
      stageRole = "Domiciliario";
    }

    stageMetrics[order.state] = {
      userId,
      userName,
      userRole: stageRole,
      durationMinutes: minutesSpent,
      completedAt: now.toISOString()
    };

    const stateUpdatedAt = now.toISOString();
    if (!order.products || !Array.isArray(order.products)) {
      await updateItem(order.id, { state: "listo / caja", stateUpdatedAt, stageDurations, stageMetrics });
    } else {
      const completedProducts = order.products.map(p => ({ ...p, completed: true }));
      await updateItem(order.id, { state: "listo / caja", products: completedProducts, stateUpdatedAt, stageDurations, stageMetrics });
    }
  };

  const handleChangeStatus = async (order: Order, state: OrderStatus) => {
    if (!order.id) return;
    const now = new Date();
    const prevDate = (order as any).stateUpdatedAt ? new Date((order as any).stateUpdatedAt) : getOrderDate(order);
    const minutesSpent = differenceInMinutes(now, prevDate);

    const stageDurations = {
      ...(order.stageDurations || {}),
      [order.state]: (order.stageDurations?.[order.state] || 0) + minutesSpent
    };

    const stageMetrics = {
      ...(order.stageMetrics || {})
    };

    let userId = userDoc?.uid || userDoc?.id || "unknown";
    let userName = userDoc?.name || userDoc?.displayName || userDoc?.email || "Desconocido";
    let stageRole = activeRole;

    if (order.state === "enPreparacion" && order.preparadorId) {
      userId = order.preparadorId;
      userName = order.preparadorName || userName;
      stageRole = "Preparación";
    } else if (order.state === "enEmpaque") {
      userId = userDoc?.id || userDoc?.uid || "unknown";
      userName = userDoc?.name || userDoc?.displayName || userDoc?.email || "Empacador";
      stageRole = "Empaque";
    } else if (order.state === "enCamino" && order.deliveryDriverId) {
      userId = order.deliveryDriverId;
      userName = order.deliveryDriverName || userName;
      stageRole = "Domiciliario";
    }

    stageMetrics[order.state] = {
      userId,
      userName,
      userRole: stageRole,
      durationMinutes: minutesSpent,
      completedAt: now.toISOString()
    };

    const updates: Partial<Order> = { state, stateUpdatedAt: now.toISOString(), stageDurations, stageMetrics };

    if (order.state === "enEmpaque") {
      updates.empacadorId = userDoc?.id || userDoc?.uid || "unknown";
      updates.empacadorName = userDoc?.name || userDoc?.displayName || userDoc?.email || "Empacador";
    }

    if (state === "enEmpaque") {
      updates.cookId = order.preparadorId || userDoc?.id || userDoc?.uid || "unknown";
      updates.cookName = order.preparadorName || userDoc?.name || userDoc?.displayName || "Cocinero";
    }

    await updateItem(order.id, updates);
  };

  const handleAssignPreparador = async (orderOrId: Order | string, prepId: string, prepName: string) => {
    const targetOrder = typeof orderOrId === "string" ? orders.find(o => o.id === orderOrId) : orderOrId;
    const masaUid = userDoc?.uid || userDoc?.id || "unknown";
    const masaName = userDoc?.name || userDoc?.displayName || userDoc?.email || "Masa";

    if (!targetOrder || !targetOrder.id) {
      if (typeof orderOrId === "string") {
        await updateItem(orderOrId, {
          preparadorId: prepId,
          preparadorName: prepName,
          masaUserId: masaUid,
          masaUserName: masaName,
          state: "enPreparacion"
        });
      }
      return;
    }

    const now = new Date();
    const prevDate = (targetOrder as any).stateUpdatedAt ? new Date((targetOrder as any).stateUpdatedAt) : getOrderDate(targetOrder);
    const minutesSpent = differenceInMinutes(now, prevDate);

    const stageDurations = {
      ...(targetOrder.stageDurations || {}),
      [targetOrder.state]: (targetOrder.stageDurations?.[targetOrder.state] || 0) + minutesSpent
    };

    const stageMetrics = {
      ...(targetOrder.stageMetrics || {})
    };

    // 1. Registrar la métrica de Masa (usuario de Masa que asignó el preparador y tiempo transcurrido en Masa)
    stageMetrics["enMasa"] = {
      userId: targetOrder.masaUserId || masaUid,
      userName: targetOrder.masaUserName || masaName,
      userRole: "Masa",
      durationMinutes: minutesSpent,
      completedAt: now.toISOString()
    };

    // 2. Registrar la métrica de Preparación con el preparador asignado
    stageMetrics["enPreparacion"] = {
      userId: prepId,
      userName: prepName,
      userRole: "Preparación",
      durationMinutes: 0,
      completedAt: now.toISOString()
    };

    const stateUpdatedAt = now.toISOString();

    const updates: Partial<Order> = {
      preparadorId: prepId,
      preparadorName: prepName,
      masaUserId: targetOrder.masaUserId || masaUid,
      masaUserName: targetOrder.masaUserName || masaName,
      state: "enPreparacion",
      stateUpdatedAt,
      stageDurations,
      stageMetrics,
      cookId: prepId,
      cookName: prepName,
    };

    const updatedOrder: Order = {
      ...targetOrder,
      ...updates,
    };

    await updateItem(targetOrder.id, updates);
    triggerComandaPrint(updatedOrder);
  };

  const handleAssignDomiciliario = async (orderId: string, domiId: string, domiName: string) => {
    await updateItem(orderId, {
      deliveryDriverId: domiId,
      deliveryDriverName: domiName,
    });
  };

  const handleAutoCheckout = async (orderToComplete: Order) => {
    if (!restaurantId) return;
    try {
      const batch = writeBatch(db);
      const todayStr = new Date().toLocaleDateString();

      for (const p of (orderToComplete.products || [])) {
        if (!p.productId || !p.variantId || p.productId === "__standalone__") continue;

        const productRef = doc(db, "restaurants", restaurantId, "products", p.productId);
        const snap = await getDoc(productRef);

        if (snap.exists()) {
          const pData = snap.data() as Product;
          const vIndex = pData.variants?.findIndex(v => v.id === p.variantId);

          if (vIndex !== undefined && vIndex > -1) {
            let variant = pData.variants[vIndex];
            const qty = Number(p.quantity || 1);

            if (variant.lastStockResetDate !== todayStr) {
              variant.initialDailyStock = variant.stock;
              variant.lastStockResetDate = todayStr;
            }

            variant.stock = Math.max(0, (variant.stock || 0) - qty);
            pData.variants[vIndex] = variant;
            batch.update(productRef, { variants: pData.variants });

            const invId = variant.inventoryItemId || pData.inventoryItemId;
            if (invId) {
              const invRef = doc(db, "restaurants", restaurantId, "inventory", invId);
              const invSnap = await getDoc(invRef);

              if (invSnap.exists()) {
                const invData = invSnap.data() as any;
                const newInvStock = (invData.currentStock || 0) - qty;

                const movement = {
                  type: "sale",
                  quantity: -qty,
                  date: new Date(),
                  user: userDoc?.name || "Sistema",
                  description: `Venta Domicilio: ${p.productName} (${orderToComplete.name})`,
                };

                batch.update(invRef, {
                  currentStock: newInvStock,
                  movements: [...(invData.movements || []), movement]
                });
              }
            }
          }
        }
      }

      const now = new Date();
      const prevDate = (orderToComplete as any).stateUpdatedAt ? new Date((orderToComplete as any).stateUpdatedAt) : getOrderDate(orderToComplete);
      const minutesSpent = differenceInMinutes(now, prevDate);

      const stageDurations = {
        ...(orderToComplete.stageDurations || {}),
        [orderToComplete.state]: (orderToComplete.stageDurations?.[orderToComplete.state] || 0) + minutesSpent
      };

      const stageMetrics = {
        ...(orderToComplete.stageMetrics || {})
      };

      let userId = userDoc?.uid || userDoc?.id || "unknown";
      let userName = userDoc?.name || userDoc?.displayName || userDoc?.email || "Desconocido";
      let stageRole = activeRole;

      if (orderToComplete.state === "enPreparacion" && orderToComplete.preparadorId) {
        userId = orderToComplete.preparadorId;
        userName = orderToComplete.preparadorName || userName;
        stageRole = "Preparación";
      } else if (orderToComplete.state === "enEmpaque") {
        userId = (orderToComplete as any).empacadorId || userDoc?.uid || userDoc?.id || "unknown";
        userName = (orderToComplete as any).empacadorName || userDoc?.name || userDoc?.displayName || "Empacador";
        stageRole = "Empaque";
      } else if (orderToComplete.state === "enCamino" && orderToComplete.deliveryDriverId) {
        userId = orderToComplete.deliveryDriverId;
        userName = orderToComplete.deliveryDriverName || userName;
        stageRole = "Domiciliario";
      }

      stageMetrics[orderToComplete.state] = {
        userId,
        userName,
        userRole: stageRole,
        durationMinutes: minutesSpent,
        completedAt: now.toISOString()
      };

      const rawOrder = {
        ...orderToComplete,
        stageDurations,
        stageMetrics,
        stateUpdatedAt: now.toISOString(),
        paymentMethod: orderToComplete.paymentMethod || "Efectivo",
        state: "completada",
        dateSuccess: new Date(),
      };

      const cleanOrder = removeUndefined(rawOrder) as Order;
      const successRef = doc(collection(db, "restaurants", restaurantId, "orderssuccess"));
      batch.set(successRef, cleanOrder);

      if (orderToComplete.id) {
        const orderRef = doc(db, "restaurants", restaurantId, "orders", orderToComplete.id);
        batch.delete(orderRef);
      }

      await batch.commit();
    } catch (error: any) {
      console.error("Error auto-facturando domiciliario:", error);
      alert(`Error al facturar la orden: ${error?.message || "Error desconocido"}`);
    }
  };

  const handleTabChange = (_: any, newValue: number) => {
    setTabValue(newValue);
  };







  /* 📂 Filtrado y Ordenamiento */

  // Verifica si TODOS los productos de una orden son bebidas (usando mapa de categorías)
  // Para el cocinero: si la orden es solo bebidas, no debe aparecer.
  const isAllDrinksOrder = (order: Order): boolean => {
    if (!order.products || order.products.length === 0) return false;
    return order.products.every(p => {
      const productDef = allProducts.find(prod => prod.id === p.productId);
      return (
        isDrink(p.category) ||
        isDrink(productDef?.category)
      );
    });
  };

  const counts = useMemo(() => {
    let verificando = 0;
    let preparacion = 0;
    let empaque = 0;
    let domicilios = 0;
    orders.forEach(o => {
      const isDrinks = isAllDrinksOrder(o);
      if (userRole === "cocinero" && isDrinks) return;

      if (o.state === "verificando_pago") verificando++;
      if (["pendiente", "enProceso", "enMasa", "enPreparacion", "enEmpaque"].includes(o.state)) preparacion++;
      if (o.state === "enEmpaque" || (!!o.preparadorId && ["enPreparacion", "enMasa", "enProceso", "pendiente"].includes(o.state))) empaque++;
      if (["esperando_domiciliario", "enCamino"].includes(o.state)) domicilios++;
    });
    return { verificando, preparacion, empaque, domicilios };
  }, [orders, userRole, effectiveRole, activeRole, allProducts]);

  const filteredOrders = useMemo(() => {
    const sorted = [...orders].sort((a, b) => {
      const dateA = getOrderDate(a);
      const dateB = getOrderDate(b);
      return dateA.getTime() - dateB.getTime();
    });

    let result = sorted;

    switch (tabValue) {
      case 0: // Verificación
        result = sorted.filter(o => o.state === "verificando_pago");
        break;
      case 2: // Preparacion
        result = sorted.filter(o => {
          const isEnPrep = ["pendiente", "enProceso", "enMasa", "enPreparacion", "enEmpaque"].includes(o.state) && (userRole === "cocinero" ? !isAllDrinksOrder(o) : true);
          if (!isEnPrep) return false;

          const isAdmin = userDoc?.isAdmin === true || userRole === "admin";
          // Si el usuario es Administrador: ve ABSOLUTAMENTE TODO sin ningún tipo de restricción
          if (isAdmin) {
            return true;
          }

          const isMasaRole = effectiveRole.includes("masa") || userRole === "masa";
          const isPrepRole = effectiveRole.includes("prep") || userRole === "preparacion";

          // Si el usuario tiene el rol de MASA: le aparecen TODAS las órdenes en preparación (libres y asignadas)
          if (isMasaRole) {
            return true;
          }

          // Si el usuario tiene el rol de PREPARACIÓN: le aparece SOLO el pedido asignado a él, o si está libre si no tiene pedido asignado
          if (isPrepRole) {
            const myUid = userDoc?.uid || userDoc?.id;
            const myAssignedOrders = sorted.filter(ord => ["pendiente", "enProceso", "enMasa", "enPreparacion", "enEmpaque"].includes(ord.state) && ord.preparadorId === myUid);
            if (myAssignedOrders.length > 0) {
              return o.preparadorId === myUid;
            } else {
              return !o.preparadorId;
            }
          }

          return true;
        });
        break;
      case 3: // Empaque
        result = sorted.filter(o => (o.state === "enEmpaque" || (!!o.preparadorId && ["enPreparacion", "enMasa", "enProceso", "pendiente"].includes(o.state))) && (userRole === "cocinero" ? !isAllDrinksOrder(o) : true));
        break;
      case 5: // Domicilios
        result = sorted.filter(o => ["esperando_domiciliario", "enCamino"].includes(o.state) && (userRole === "cocinero" ? !isAllDrinksOrder(o) : true));
        break;
      case 4: // Caja e Historial
        result = sorted.filter(o => ["entregado", "completada", "cancelada", "porCobrar", "listo / caja"].includes(o.state));
        break;
      default:
        result = sorted;
        break;
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(o => {
        const name = (o.name || (o as any).customerName || '').toLowerCase();
        const table = (o.tableNumber || '').toLowerCase();
        const code = (o.supportCode || o.id || '').toLowerCase();
        const phone = ((o as any).phoneNumber || (o as any).phone || (o as any).customerPhone || (o as any).clientPhone || '').toLowerCase();
        const address = (o.address || '').toLowerCase();
        const neighborhood = ((o as any).neighborhoodName || (o as any).neighborhood || '').toLowerCase();
        const driver = ((o as any).deliveryDriverName || '').toLowerCase();
        const prep = (o.preparadorName || '').toLowerCase();
        const creator = (o.createdByName || '').toLowerCase();
        const notes = (o.notes || '').toLowerCase();
        const payment = (o.paymentMethod || '').toLowerCase();
        const products = (o.products || []).map(p => `${p.productName || ''} ${(p as any).notes || ''}`).join(' ').toLowerCase();

        return name.includes(q) ||
          table.includes(q) ||
          code.includes(q) ||
          phone.includes(q) ||
          address.includes(q) ||
          neighborhood.includes(q) ||
          driver.includes(q) ||
          prep.includes(q) ||
          creator.includes(q) ||
          notes.includes(q) ||
          payment.includes(q) ||
          products.includes(q) ||
          JSON.stringify(o).toLowerCase().includes(q);
      });
    }

    return result;
  }, [orders, tabValue, userRole, effectiveRole, activeRole, userDoc, searchQuery]);

  return (
    <Box sx={{ maxWidth: 1500, mx: "auto", pb: 6, px: { xs: 0.5, sm: 1.5, md: 2 }, width: "100%", overflowX: "hidden", boxSizing: "border-box" }}>
      {/* SECCIÓN DE SELECCIÓN DE ROL EN TURNO (ARRIBA DE LAS BASES) - RESPONSIVE & TEMA CLARO */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: { xs: 2.5, sm: 3 },
          borderRadius: 4,
          background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          color: "#0f172a",
          border: "1px solid #e2e8f0",
          boxShadow: "0 8px 24px -4px rgba(15, 23, 42, 0.06)",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 2
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            onClick={handleLogoutClick}
            sx={{
              bgcolor: "primary.main",
              color: "#ffffff",
              width: { xs: 40, sm: 46 },
              height: { xs: 40, sm: 46 },
              fontWeight: 900,
              fontSize: { xs: "1rem", sm: "1.15rem" },
              boxShadow: "0 4px 14px rgba(234, 88, 12, 0.3)",
              cursor: "pointer",
              transition: "transform 0.2s",
              "&:hover": {
                transform: "scale(1.08)"
              }
            }}
          >
            {(userDoc?.name || userDoc?.displayName || "U").charAt(0).toUpperCase()}
          </Avatar>
          <Menu
            anchorEl={logoutAnchorEl}
            open={Boolean(logoutAnchorEl)}
            onClose={handleCloseLogoutMenu}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: "visible",
                filter: "drop-shadow(0px 8px 24px rgba(15,23,42,0.12))",
                mt: 1.5,
                borderRadius: 3.5,
                minWidth: 230,
                p: 1,
                border: "1px solid #e2e8f0",
              },
            }}
            transformOrigin={{ horizontal: "left", vertical: "top" }}
            anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
          >
            <Box sx={{ px: 1.5, py: 1 }}>
              <Typography variant="subtitle2" fontWeight={900} color="#0f172a">
                {userDoc?.name || userDoc?.displayName || "Usuario"}
              </Typography>
              {userDoc?.email && (
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  {userDoc.email}
                </Typography>
              )}
              {userDoc?.role && (
                <Chip
                  label={userDoc.role.toUpperCase()}
                  size="small"
                  sx={{
                    mt: 0.8,
                    bgcolor: "primary.50",
                    color: "primary.main",
                    fontWeight: 800,
                    fontSize: "0.68rem",
                    height: 20
                  }}
                />
              )}
            </Box>
            <Divider sx={{ my: 1 }} />
            <MenuItem
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                color: "#ef4444",
                fontWeight: 800,
                gap: 1.5,
                py: 1,
                "&:hover": {
                  bgcolor: "#fef2f2"
                }
              }}
            >
              <Logout fontSize="small" />
              Cerrar Sesión
            </MenuItem>
          </Menu>
          <Box>
            <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
              👤 Usuario: <span style={{ color: "#0f172a", fontWeight: 900 }}>{userDoc?.name || userDoc?.displayName || "Desconocido"}</span>
            </Typography>
            <Typography variant="subtitle1" fontWeight={800} sx={{ color: "#0f172a", lineHeight: 1.2, fontSize: { xs: "0.95rem", sm: "1rem" } }}>
              ¿Qué rol estás desempeñando en este momento?
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ gap: 1, width: { xs: "100%", sm: "auto" } }}>
          {["Masa", "Preparación", "Empaque"].map((roleName) => {
            const isSelected = activeRole.toLowerCase() === roleName.toLowerCase();
            return (
              <Chip
                key={roleName}
                label={roleName}
                onClick={() => handleRoleChange(roleName)}
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "0.8rem", sm: "0.85rem" },
                  flex: { xs: "1 1 auto", sm: "none" },
                  textAlign: "center",
                  px: { xs: 1, sm: 1.5 },
                  py: 2,
                  borderRadius: 3,
                  cursor: "pointer",
                  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                  bgcolor: isSelected ? "primary.main" : "#f1f5f9",
                  color: isSelected ? "#ffffff" : "#475569",
                  border: isSelected ? "2px solid #ea580c" : "1px solid #cbd5e1",
                  boxShadow: isSelected ? "0 4px 14px rgba(234, 88, 12, 0.3)" : "none",
                  "&:hover": {
                    bgcolor: isSelected ? "primary.dark" : "#e2e8f0",
                    transform: "translateY(-2px)",
                  }
                }}
              />
            );
          })}
        </Stack>
      </Paper>

      {/* HEADER BALANCEADO */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={2}
        mb={{ xs: 3, sm: 5 }}
        mt={2}
      >
        <Box>
          <Typography variant="h4" fontWeight={900} sx={{ letterSpacing: "-0.03em", fontSize: { xs: "1.65rem", sm: "2.125rem" } }}>
            Gestión de Órdenes
          </Typography>
          <Typography color="text.secondary" variant="body1" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
            Control en tiempo real de tu cocina y repartos
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: { xs: "100%", md: "auto" }, overflowX: "auto" }}>
          <Paper
            elevation={0}
            sx={{
              p: 0.5,
              borderRadius: 3,
              bgcolor: "#f1f5f9",
              border: "1px solid #e2e8f0",
              width: "100%",
              overflowX: "auto"
            }}
          >
            <Tabs
              value={allowedTabs.includes(tabValue) ? tabValue : false}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: 44,
                "& .MuiTabs-indicator": { height: "100%", borderRadius: 2, zIndex: 0, bgcolor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                "& .MuiTab-root": { minHeight: 44, zIndex: 1, textTransform: "none", fontWeight: 700, px: { xs: 2, sm: 3 }, fontSize: { xs: "0.85rem", sm: "0.95rem" }, borderRadius: 2 }
              }}
            >
              {allowedTabs.includes(0) && <Tab value={0} label={`Verificación (${counts.verificando})`} />}
              {allowedTabs.includes(2) && <Tab value={2} label={`Prep. (${counts.preparacion})`} />}
              {allowedTabs.includes(3) && <Tab value={3} label={`Empaque (${counts.empaque})`} />}
              {allowedTabs.includes(5) && <Tab value={5} label={`Domicilios (${counts.domicilios})`} />}
              {allowedTabs.includes(4) && <Tab value={4} label="Caja / Historial" />}
              {allowedTabs.includes(6) && <Tab value={6} label="Tiempos" />}
            </Tabs>
          </Paper>
        </Box>
      </Stack>

      {/* BUSCADOR (En Empaque, Domicilios y Tiempos) */}
      {(tabValue === 3 || tabValue === 5 || tabValue === 6) && (
        <Fade in timeout={500}>
          <Box mb={3} sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
            <TextField
              fullWidth
              placeholder="Buscar por cliente, teléfono, dirección, barrio, código, domiciliario, producto..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery("")}>
                      <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
              sx={{
                bgcolor: '#ffffff',
                borderRadius: 4,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 4,
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                  '& fieldset': { borderColor: '#e2e8f0', transition: 'all 0.2s' },
                  '&:hover fieldset': { borderColor: '#cbd5e1' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: '2px' },
                }
              }}
            />
          </Box>
        </Fade>
      )}

      {/* GRILLA DE ÓRDENES */}
      <Grid container spacing={2}>
        {filteredOrders.length === 0 && !(tabValue === 2 && (userRole === "admin" || userDoc?.isAdmin || effectiveRole === "admin")) ? (
          <Grid size={12}>
            <Fade in timeout={800}>
              <Box
                sx={{
                  textAlign: "center",
                  py: 12,
                  bgcolor: "#fff",
                  borderRadius: 5,
                  border: "2px dashed #e2e8f0"
                }}
              >
                <ShoppingBag sx={{ fontSize: 70, color: "#cbd5e1", mb: 3 }} />
                <Typography variant="h5" color="text.secondary" fontWeight={800} mb={1}>
                  No hay órdenes actuales
                </Typography>
                <Typography variant="body1" color="text.disabled">
                  Las nuevas órdenes aparecerán aquí automáticamente
                </Typography>
              </Box>
            </Fade>
          </Grid>
        ) : tabValue === 2 && (userRole === "admin" || userDoc?.isAdmin || effectiveRole === "admin") ? (
          <Grid size={12}>
            <Grid container spacing={2}>
              {/* COLUMNA IZQ: Órdenes sin asignar */}
              <Grid size={{ xs: 12, md: 4, lg: 3 }}>
                <Typography variant="h6" fontWeight={800} mb={2} color="primary.main">
                  Órdenes Sin Asignar
                </Typography>
                <Stack spacing={1.5}>
                  {filteredOrders.filter(o => !o.preparadorId).map((order) => (
                    <OrderCard showTiempos={tabValue === 6} userName={userDoc?.name} onCancelOrderPrompt={(order, onConfirm) => setCancelPrompt({ order, onConfirm })}
                      key={order.id}
                      order={order}
                      allProducts={allProducts}
                      allFlavors={allFlavors}
                      otherProductsList={otherProductsList}
                      userRole={userRole}
                      canDelete={canDelete}
                      onUpdateStatus={handleChangeStatus}
                      onUpdateOrder={updateItem}
                      onComplete={handleSendToCashier}
                      onAutoCheckout={handleAutoCheckout}
                      onDelete={deleteItem}
                      onEdit={(id) => navigate(`/${restaurantId}/edit-order/${id}`)}
                      isDrinkFn={isDrink}
                      prepUsers={users.filter(u => u.role === "preparacion")}
                      onAssignPreparador={handleAssignPreparador}
                      onPrintComanda={triggerComandaPrint}
                      compact={true}
                    />
                  ))}
                  {filteredOrders.filter(o => !o.preparadorId).length === 0 && (
                    <Typography color="text.secondary">No hay órdenes sin asignar.</Typography>
                  )}
                </Stack>
              </Grid>

              {/* COLUMNA DER: Preparadores */}
              <Grid size={{ xs: 12, md: 8, lg: 9 }}>
                <Typography variant="h6" fontWeight={800} mb={2} color="secondary.main">
                  Preparadores y sus Órdenes
                </Typography>
                <Grid container spacing={1.5}>
                  {users.filter(u => u.role === "preparacion").map(user => {
                    const uid = user.uid || (user as any).id;
                    const assignedOrders = orders.filter(o => o.preparadorId === uid && ["pendiente", "enProceso", "enMasa", "enPreparacion", "enEmpaque"].includes(o.state));
                    const prepAnalytics = analytics.workerMetrics.find(m => m.id === uid);
                    const avgMins = prepAnalytics?.avgMins || 0;

                    return (
                      <Grid key={uid} size={{ xs: 12, sm: 6, lg: 4 }}>
                        <Box sx={{ bgcolor: "#f8fafc", p: 1.5, borderRadius: 3, border: "1px solid #e2e8f0", height: "100%" }}>
                          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                            <Avatar src={user.perfil} sx={{ width: 28, height: 28 }} />
                            <Typography variant="subtitle2" fontWeight={800} noWrap sx={{ flexGrow: 1 }}>{user.name}</Typography>

                            {/* Órdenes actuales */}
                            <Chip label={assignedOrders.length} size="small" color="primary" sx={{ height: 20, fontSize: "0.7rem", fontWeight: 800 }} />

                            {/* Promedio de Tiempo */}
                            <MuiTooltip title="Tiempo promedio de preparación de hoy">
                              <Chip
                                icon={<AccessTime sx={{ fontSize: 12 }} />}
                                label={`${avgMins}m`}
                                size="small"
                                color={avgMins > 30 ? "error" : "success"}
                                sx={{ height: 20, fontSize: "0.7rem", fontWeight: 800, pl: 0.5 }}
                              />
                            </MuiTooltip>
                          </Stack>
                          {assignedOrders.length === 0 ? (
                            <Typography variant="caption" color="text.disabled">Sin órdenes asignadas.</Typography>
                          ) : (
                            <Stack spacing={1}>
                              {assignedOrders.map(order => (
                                <OrderCard showTiempos={tabValue === 6} userName={userDoc?.name} onCancelOrderPrompt={(order, onConfirm) => setCancelPrompt({ order, onConfirm })}
                                  key={order.id}
                                  order={order}
                                  allProducts={allProducts}
                                  allFlavors={allFlavors}
                                  otherProductsList={otherProductsList}
                                  userRole={userRole}
                                  canDelete={canDelete}
                                  onUpdateStatus={handleChangeStatus}
                                  onUpdateOrder={updateItem}
                                  onComplete={handleSendToCashier}
                                  onDelete={deleteItem}
                                  onEdit={(id) => navigate(`/${restaurantId}/edit-order/${id}`)}
                                  isDrinkFn={isDrink}
                                  onPrintComanda={triggerComandaPrint}
                                  compact={true}
                                />
                              ))}
                            </Stack>
                          )}
                        </Box>
                      </Grid>
                    )
                  })}
                  {users.filter(u => u.role === "preparacion").length === 0 && (
                    <Grid size={12}>
                      <Typography color="text.secondary">No hay usuarios con el rol "Preparación".</Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ) : tabValue === 5 ? (
          <Grid size={12}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" fontWeight={800} mb={2} color="warning.main">
                  Esperando Domiciliario
                </Typography>
                <Stack spacing={2}>
                  {filteredOrders.filter(o => o.state === "esperando_domiciliario").map(order => (
                    <OrderCard showTiempos={tabValue === 6} userName={userDoc?.name} onCancelOrderPrompt={(order, onConfirm) => setCancelPrompt({ order, onConfirm })}
                      key={order.id}
                      order={order}
                      allProducts={allProducts}
                      allFlavors={allFlavors}
                      otherProductsList={otherProductsList}
                      userRole={userRole}
                      canDelete={canDelete}
                      onUpdateStatus={handleChangeStatus}
                      onUpdateOrder={updateItem}
                      onComplete={handleSendToCashier}
                      onAutoCheckout={handleAutoCheckout}
                      onDelete={deleteItem}
                      onEdit={(id) => navigate(`/${restaurantId}/edit-order/${id}`)}
                      isDrinkFn={isDrink}
                      prepUsers={users.filter(u => u.role === "preparacion")}
                      domiUsers={users.filter(u => u.role === "domiciliario")}
                      onAssignPreparador={handleAssignPreparador}
                      onAssignDomiciliario={handleAssignDomiciliario}
                      onPrintComanda={triggerComandaPrint}
                      dense={true}
                    />
                  ))}
                  {filteredOrders.filter(o => o.state === "esperando_domiciliario").length === 0 && (
                    <Typography color="text.secondary">No hay órdenes esperando domiciliario.</Typography>
                  )}
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" fontWeight={800} mb={2} color="info.main">
                  En Camino
                </Typography>
                <Stack spacing={2}>
                  {filteredOrders.filter(o => o.state === "enCamino").map(order => (
                    <OrderCard showTiempos={tabValue === 6} userName={userDoc?.name} onCancelOrderPrompt={(order, onConfirm) => setCancelPrompt({ order, onConfirm })}
                      key={order.id}
                      order={order}
                      allProducts={allProducts}
                      allFlavors={allFlavors}
                      otherProductsList={otherProductsList}
                      userRole={userRole}
                      canDelete={canDelete}
                      onUpdateStatus={handleChangeStatus}
                      onUpdateOrder={updateItem}
                      onComplete={handleSendToCashier}
                      onAutoCheckout={handleAutoCheckout}
                      onDelete={deleteItem}
                      onEdit={(id) => navigate(`/${restaurantId}/edit-order/${id}`)}
                      isDrinkFn={isDrink}
                      prepUsers={users.filter(u => u.role === "preparacion")}
                      domiUsers={users.filter(u => u.role === "domiciliario")}
                      onAssignPreparador={handleAssignPreparador}
                      onAssignDomiciliario={handleAssignDomiciliario}
                      onPrintComanda={triggerComandaPrint}
                      dense={true}
                    />
                  ))}
                  {filteredOrders.filter(o => o.state === "enCamino").length === 0 && (
                    <Typography color="text.secondary">No hay órdenes en camino.</Typography>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          filteredOrders.map((order) => (
            <Grid key={order.id} size={{ xs: 12, md: 6, lg: 4 }}>
              <OrderCard showTiempos={tabValue === 6} userName={userDoc?.name} onCancelOrderPrompt={(order, onConfirm) => setCancelPrompt({ order, onConfirm })}
                order={order}
                allProducts={allProducts}
                allFlavors={allFlavors}
                otherProductsList={otherProductsList}
                userRole={userRole}
                canDelete={canDelete}
                onUpdateStatus={handleChangeStatus}
                onUpdateOrder={updateItem}
                onComplete={handleSendToCashier}
                onAutoCheckout={handleAutoCheckout}
                onDelete={deleteItem}
                onEdit={(id) => navigate(`/${restaurantId}/edit-order/${id}`)}
                isDrinkFn={isDrink}
                prepUsers={users.filter(u => u.role === "preparacion")}
                domiUsers={users.filter(u => u.role === "domiciliario")}
                onAssignPreparador={handleAssignPreparador}
                onAssignDomiciliario={handleAssignDomiciliario}
                onPrintComanda={triggerComandaPrint}
                hideActions={tabValue === 6}
              />
            </Grid>
          ))
        )}
      </Grid>
      <CancelOrderDialog
        open={!!cancelPrompt}
        order={cancelPrompt?.order || null}
        onClose={() => setCancelPrompt(null)}
        onConfirm={(reason) => {
          if (cancelPrompt?.onConfirm) cancelPrompt.onConfirm(reason);
        }}
      />
      {/* Hidden Comanda Receipt for Printing */}
      <Box sx={{ display: "none" }}>
        {comandaOrder && (
          <ComandaReceipt ref={comandaRef} order={comandaOrder} />
        )}
      </Box>
    </Box>
  );
}

/* =======================
   📦 Tarjeta de Orden (Refinada)
 ======================= */

function OrderCard({ order: originalOrder, allProducts, allFlavors, otherProductsList = [], userRole, userName, canDelete, onUpdateStatus, onComplete, onDelete, onEdit, onUpdateOrder, isDrinkFn, prepUsers, onAssignPreparador, domiUsers, onAssignDomiciliario, compact, dense, onAutoCheckout, hideActions, showTiempos, onCancelOrderPrompt, onPrintComanda }: {
  order: Order;
  allProducts: Product[];
  allFlavors: any[];
  otherProductsList?: OtherProduct[];
  userRole?: string;
  userName?: string;
  canDelete?: boolean;
  onUpdateStatus: (order: Order, s: OrderStatus) => void;
  onComplete: (o: Order) => void;
  onDelete: (id: string) => void;
  onEdit: (id: string) => void;
  onUpdateOrder: (id: string, updates: Partial<Order>) => Promise<void> | void;
  isDrinkFn: (categoryName?: string) => boolean;
  prepUsers?: Users[];
  onAssignPreparador?: (orderId: string, prepId: string, prepName: string) => void;
  domiUsers?: Users[];
  onAssignDomiciliario?: (orderId: string, domiId: string, domiName: string) => void;
  compact?: boolean;
  dense?: boolean;
  onAutoCheckout?: (order: Order) => void;
  hideActions?: boolean;
  showTiempos?: boolean;
  onCancelOrderPrompt?: (order: Order, onConfirm: (reason: string) => void) => void;
  onPrintComanda?: (order: Order) => void;
}) {
  const order = useMemo(() => {
    if (originalOrder.products && Array.isArray(originalOrder.products) && originalOrder.products.length > 0) {
      return originalOrder;
    }
    const products: any[] = [];

    if ((originalOrder as any).pizzas) {
      (originalOrder as any).pizzas.forEach((pizzaObj: any) => {
        const sName = pizzaObj.sizeName || pizzaObj.size || 'Tradicional';
        const slicesArr = pizzaObj.slices || pizzaObj.flavors || [];
        const qty = pizzaObj.quantity ?? 1;

        let addNames: any[] = [];
        if (pizzaObj.additionNames) {
          addNames = Array.isArray(pizzaObj.additionNames)
            ? pizzaObj.additionNames.map((n: any) => ({ id: Math.random().toString(), name: n }))
            : Object.values(pizzaObj.additionNames).map((n: any) => ({ id: Math.random().toString(), name: n?.name || n }));
        }

        let flavorStr = "";
        if (slicesArr.length > 0) {
          const firstS = slicesArr[0];
          const areAllIdentical = slicesArr.length > 1 && slicesArr.every((s: any) => {
            const f1 = s.flavorName || s.name || s.flavorId || s.id || "";
            const f2 = firstS.flavorName || firstS.name || firstS.flavorId || firstS.id || "";
            if (f1 !== f2) return false;
            const ing1 = (s.ingredients || []).slice().sort().join(',');
            const ing2 = (firstS.ingredients || []).slice().sort().join(',');
            return ing1 === ing2;
          });

          if (areAllIdentical && firstS) {
            let name = firstS.flavorName || firstS.name || firstS.flavorId || firstS.id || "";
            let ings = (firstS.ingredients && Array.isArray(firstS.ingredients) && firstS.ingredients.length > 0)
              ? ` (${firstS.ingredients.join(', ')})`
              : '';
            if (name === "build-your-own" || name === "Arma tu pizza" || name === "Personalizada") {
              name = "Personalizada" + ings;
            } else if (ings) {
              name = name + ings;
            }
            flavorStr = `🍕 ${name}`;
          } else {
            flavorStr = slicesArr.map((s: any) => {
              const portionMap: Record<string, string> = {
                whole: 'Entera', left: 'Mitad Izq.', right: 'Mitad Der.', 'top-left': '¼ Sup. Izq.', 'top-right': '¼ Sup. Der.', 'bottom-left': '¼ Inf. Izq.', 'bottom-right': '¼ Inf. Der.'
              };
              let p = s.portion ? (portionMap[s.portion] || s.portion) : "";
              let name = s.flavorName || s.name || s.id || "";
              let ings = (s.ingredients && Array.isArray(s.ingredients) && s.ingredients.length > 0)
                ? ` (${s.ingredients.join(', ')})`
                : '';
              if (name === "build-your-own" || name === "Arma tu pizza") {
                name = "Personalizada" + ings;
              } else if (ings) {
                name = name + ings;
              }
              return `${p}: 🍕 ${name}`;
            }).join('\n');
          }
        }

        products.push({
          productId: pizzaObj.id || Math.random().toString(),
          productName: sName && sName !== 'Tradicional' ? `Pizza ${sName}` : 'Pizza',
          variantId: '',
          variantLabel: flavorStr,
          price: pizzaObj.itemPrice || 0,
          quantity: qty,
          image: '',
          category: 'pizzas',
          completed: false,
          additions: addNames,
          sliceMode: pizzaObj.sliceMode
        });
      });
    }

    if ((originalOrder as any).otherProductNames) {
      const qtys = (originalOrder as any).otherProducts || {};
      const prices = (originalOrder as any).otherProductPrices || {};
      Object.entries((originalOrder as any).otherProductNames).forEach(([id, name]) => {
        let itemPrice = prices[id] ?? 0;
        if (!itemPrice || itemPrice === 0) {
          if (id.includes(':')) {
            const [prodId, variantKey] = id.split(':');
            const prod = (otherProductsList || []).find((p: any) => p.id === prodId);
            const variant = prod?.variants?.find((v: any) => (v.id || v.name) === variantKey || v.name === variantKey);
            itemPrice = (prod?.price ?? 0) + (variant?.price ?? 0);
          } else {
            const prod = (otherProductsList || []).find((p: any) => p.id === id);
            itemPrice = prod?.price ?? 0;
          }
        }
        products.push({
          productId: id,
          productName: String(name),
          variantId: '',
          variantLabel: '',
          price: itemPrice,
          quantity: qtys[id] || 1,
          image: '',
          category: 'other',
          completed: false
        });
      });
    }

    if ((originalOrder as any).additionNames) {
      const names = Array.isArray((originalOrder as any).additionNames)
        ? (originalOrder as any).additionNames
        : Object.values((originalOrder as any).additionNames);

      names.forEach((add: any) => {
        const nameStr = typeof add === 'string' ? add : add?.name || 'Adición';
        products.push({
          productId: Math.random().toString(),
          productName: `ADICIÓN GENERAL: ${nameStr}`,
          variantId: '',
          variantLabel: '',
          price: 0,
          quantity: 1,
          image: '',
          category: 'addition',
          completed: false
        });
      });
    }

    return { ...originalOrder, products: products.length > 0 ? products : (originalOrder.products || []) };
  }, [originalOrder, otherProductsList]);

  const date = getOrderDate(order);
  const minutes = differenceInMinutes(new Date(), date);

  const stateDate = (order as any).stateUpdatedAt ? new Date((order as any).stateUpdatedAt) : date;
  const stateMinutes = differenceInMinutes(new Date(), stateDate);

  const { restaurantId } = useParams<{ restaurantId: string }>();
  const { config } = useConfig(restaurantId);
  const isDelayed = minutes >= config.orderDelayThreshold && !["entregado", "completada", "cancelada"].includes(order.state);

  const handleCancelOrder = async () => {
    if (!restaurantId || !order.id) return;

    if (onCancelOrderPrompt) {
      onCancelOrderPrompt(order, async (reason) => {
        try {
          const rawOrder = {
            ...order,
            state: "cancelada" as OrderStatus,
            dateSuccess: new Date(),
            cancelReason: reason.trim() || "Sin motivo",
            cancelledBy: userName || "Administrador",
          };
          const cleanOrder = removeUndefined(rawOrder);
          await addDoc(collection(db, "restaurants", restaurantId, "orderssuccess"), cleanOrder);
          onDelete(order.id!);
        } catch (error) {
          console.error("Error anulando la orden:", error);
          alert("Error al anular la orden.");
        }
      });
    }
  };

  const totalGeneral = useMemo(() => {
    const productsSum = order.products?.reduce(
      (acc, p) => acc + Number(p.total ?? Number(p.price || 0) * Number(p.quantity || 1)),
      0
    );
    const delPrice = Number((order as any).deliveryPrice || (order as any).shippingCost || 0);
    if (productsSum && productsSum > 0) {
      return productsSum + delPrice;
    }
    return ((order as any).totalPrice ?? order.total ?? 0);
  }, [order]);

  const isPaidOrder = useMemo(() => {
    if (typeof (order as any).isPaid === "boolean") return (order as any).isPaid;
    if (typeof (order as any).paid === "boolean") return (order as any).paid;
    if ((order as any).paymentStatus === "paid" || (order as any).paymentStatus === "pagado") return true;
    if ((order as any).paymentStatus === "pending" || (order as any).paymentStatus === "pendiente" || (order as any).paymentStatus === "porCobrar") return false;
    if (order.state === "completada") return true;
    if (order.state === "porCobrar") return false;
    if (order.dateSuccess && order.state !== "cancelada") return true;
    return false;
  }, [order]);

  const { foodItems, drinkItems } = useMemo(() => {
    const food: typeof order.products = [];
    const drinks: typeof order.products = [];

    // Guard: órdenes del cliente Thomas Pizza no tienen 'products'
    if (!order.products || !Array.isArray(order.products)) {
      return { foodItems: food, drinkItems: drinks };
    }

    const visibleProducts = order.products;

    visibleProducts.forEach(p => {
      const storedCategory = p.category;
      const productDef = allProducts.find(prod => prod.id === p.productId);
      const lookupCategory = productDef?.category;

      const isDrinkItem =
        isDrinkFn(storedCategory) ||
        isDrinkFn(lookupCategory);

      if (isDrinkItem) {
        drinks.push(p);
      } else {
        food.push(p);
      }
    });

    return { foodItems: food, drinkItems: drinks };
  }, [order.products, allProducts, order.state, isDrinkFn]);

  const renderProduct = (p: typeof order.products[0], idx: number) => {
    const isPizza = p.category?.toLowerCase().includes("pizza") || p.productName?.toLowerCase().includes("pizza");
    const isPizzaFromApp = isPizza && p.productName?.toUpperCase().includes("DESDE APP");

    const detectedSize = (() => {
      if ((p as any).sizeName) return (p as any).sizeName;
      if ((p as any).size) return (p as any).size;
      if ((p as any).sizeLabel) return (p as any).sizeLabel;

      const sizeRegex = /(mediana|mediano|grande|pequeña|pequeño|familiar|personal|8 porciones|12 porciones|6 porciones|4 porciones)/i;
      if (p.variantLabel) {
        const matchVar = p.variantLabel.match(sizeRegex);
        if (matchVar) return matchVar[0];
        if (p.variantLabel.includes("-")) {
          const firstPart = p.variantLabel.split("-")[0].trim();
          if (firstPart) return firstPart;
        }
      }
      if (p.productName) {
        const matchProd = p.productName.match(sizeRegex);
        if (matchProd) return matchProd[0];
      }
      return p.variantLabel || "";
    })();

    return (
      <Box key={`${p.productId}-${idx}`} sx={{ mb: isPizzaFromApp ? 0.5 : 0 }}>
        {isPizzaFromApp && (
          <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ display: 'block', mb: 0.5, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.65rem' }}>
            🍕 {p.productName.toUpperCase()}
          </Typography>
        )}
        <Stack direction="row" spacing={compact ? 1 : 2} alignItems="flex-start">
          <Box
            sx={{
              bgcolor: "primary.main",
              color: "#fff",
              minWidth: 20,
              height: 20,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.75rem",
              fontWeight: 900,
              mt: 0.2
            }}
          >
            {p.quantity}
          </Box>

          <Box sx={{ flex: 1 }}>
            {(() => {
              if (isPizza) {
                let sizeStr = p.productName;
                let flavorStr = p.variantLabel || "";

                let portionType = "Entera";
                const sliceMode = (p as any).sliceMode;
                if (flavorStr.toLowerCase().includes("mitad") || sliceMode === "half") portionType = "Mitades";
                else if (flavorStr.toLowerCase().includes("cuarto") || sliceMode === "quarter") portionType = "Cuartos";
                else if (flavorStr.toLowerCase().includes("entera") || sliceMode === "whole") portionType = "Entera";

                let flavorLines: string[] = [];

                if ((p as any).slices && Array.isArray((p as any).slices) && (p as any).slices.length > 0) {
                  const slicesList = (p as any).slices;
                  const firstS = slicesList[0];
                  const areAllIdentical = slicesList.length > 1 && slicesList.every((s: any) => {
                    const f1 = s.flavorName || s.name || s.flavorId || s.id || "";
                    const f2 = firstS.flavorName || firstS.name || firstS.flavorId || firstS.id || "";
                    if (f1 !== f2) return false;
                    const ing1 = (s.ingredients || []).slice().sort().join(',');
                    const ing2 = (firstS.ingredients || []).slice().sort().join(',');
                    return ing1 === ing2;
                  });

                  if (areAllIdentical && firstS) {
                    let name = firstS.flavorName || firstS.name || firstS.flavorId || firstS.id || "";
                    let ings = (firstS.ingredients && Array.isArray(firstS.ingredients) && firstS.ingredients.length > 0)
                      ? `: ${firstS.ingredients.join(', ')}`
                      : '';
                    if (name === "build-your-own" || name === "Arma tu pizza" || name === "Personalizada") {
                      name = "Personalizada" + ings;
                    } else if (ings) {
                      name = name + ings;
                    }
                    flavorLines = [`· 🍕 ${name}`];
                  } else {
                    const portionMap: Record<string, string> = {
                      whole: 'Entera', left: 'Mitad Izq.', right: 'Mitad Der.', 'top-left': '¼ Sup. Izq.', 'top-right': '¼ Sup. Der.', 'bottom-left': '¼ Inf. Izq.', 'bottom-right': '¼ Inf. Der.'
                    };
                    flavorLines = slicesList.map((s: any) => {
                      let pName = s.portion ? (portionMap[s.portion] || s.portion) : portionType;
                      let name = s.flavorName || s.name || s.id || "";
                      let ings = (s.ingredients && Array.isArray(s.ingredients) && s.ingredients.length > 0)
                        ? `: ${s.ingredients.join(', ')}`
                        : '';
                      if (name === "build-your-own" || name === "Arma tu pizza") {
                        name = "Personalizada" + ings;
                      } else if (ings) {
                        name = name + ings;
                      }
                      return `· ${pName}: 🍕 ${name}`;
                    });
                  }
                } else {
                  const sizeRegex = /mediana|mediano|grande|pequeña|familiar|personal/i;
                  const match = flavorStr.match(sizeRegex);
                  if (match) {
                    const matchedSize = match[0];
                    const capitalizedSize = matchedSize.charAt(0).toUpperCase() + matchedSize.slice(1).toLowerCase();
                    sizeStr = `Pizza ${capitalizedSize}`;
                    flavorStr = flavorStr.replace(new RegExp(matchedSize, 'i'), '').replace(/^[-\s·•|:]+/, '').trim() || "Personalizada";
                  } else if (flavorStr.includes('-')) {
                    sizeStr = flavorStr.split('-')[0]?.trim() ? `Pizza ${flavorStr.split('-')[0].trim()}` : "Pizza Personalizada";
                    flavorStr = flavorStr.split('-').slice(1).join('-').trim() || flavorStr || "Personalizada";
                  }

                  sizeStr = sizeStr.replace(/25ml/ig, '').trim();
                  flavorStr = flavorStr.replace(/25ml/ig, '').trim();

                  let splittedFlavors = flavorStr.split(/\n|<br\s*\/?>/);
                  if (splittedFlavors.length === 1) {
                    if (flavorStr.includes(" / ")) {
                      splittedFlavors = flavorStr.split(/\s*\/\s*/);
                    } else if (flavorStr.toLowerCase().includes("mitad") || flavorStr.toLowerCase().includes("cuarto")) {
                      splittedFlavors = flavorStr.split(/,\s*(?=Mitad|Cuarto|Entera)/i);
                    }
                  }

                  flavorLines = splittedFlavors.map((s: string) => {
                    let str = s.trim();
                    if (!str) return null;
                    str = str.replace(/^[·•]\s*/, "");
                    let prefix = "";
                    if (!str.toLowerCase().includes("mitad") && !str.toLowerCase().includes("cuarto") && !str.toLowerCase().includes("entera")) {
                      prefix = `${portionType}: `;
                    }
                    if (!str.includes("🍕")) {
                      if (str.includes(":")) {
                        str = str.replace(/:\s*/, ': 🍕 ');
                      } else {
                        str = `🍕 ${str}`;
                      }
                    }
                    return `· ${prefix}${str}`;
                  }).filter(Boolean) as string[];
                }

                return (
                  <>
                    <Typography variant="body2" fontWeight={900} sx={{ lineHeight: 1.2, display: 'block', color: '#0f172a', fontSize: '0.82rem' }}>
                      {p.productName} · {portionType}
                    </Typography>
                    <Box mt={0.25} sx={{ pl: 0.75, borderLeft: '2px solid', borderColor: '#334155' }}>
                      {flavorLines.map((line: string, i: number) => (
                        <Typography key={i} variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: "0.75rem", display: 'block' }}>
                          {line}
                        </Typography>
                      ))}
                    </Box>
                  </>
                );
              }

              return (
                <>
                  <Typography variant="body2" fontWeight={800} sx={{ lineHeight: 1.2, display: 'block' }}>
                    {p.productName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: "0.7rem", display: 'block' }}>
                    {p.variantLabel}
                  </Typography>
                </>
              );
            })()}

            {p.additions && p.additions.length > 0 && (
              <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap">
                {p.additions.map((add: any) => (
                  <Chip
                    key={add.id}
                    label={`+ ${add.name}`}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      borderColor: "success.light",
                      color: "success.dark"
                    }}
                    variant="outlined"
                  />
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      </Box>
    );
  };

  const nextStateInfo = (() => {
    if (["entregado", "completada", "cancelada", "porCobrar", "listo / caja"].includes(order.state)) {
      return null;
    }

    switch (order.state) {
      case "verificando_pago":
        return { label: "Aprobar Pago", nextState: "enPreparacion" as OrderStatus };
      case "enPreparacion":
      case "enEmpaque":
        if (order.orderType === 'domicilio' || order.orderType === 'llevar') {
          return { label: "Esperando Domiciliario", nextState: "esperando_domiciliario" as OrderStatus };
        }
        return { label: "Listo / A Caja", nextState: "listo / caja" as OrderStatus };
      case "esperando_domiciliario":
        return { label: "Enviar Domiciliario", nextState: "enCamino" as OrderStatus };
      case "enCamino":
        return { label: "Marcar Entregado", nextState: "entregado" as OrderStatus };
      default:
        return { label: "Llevar a Preparación", nextState: "enPreparacion" as OrderStatus };
    }
  })();

  return (
    <Fade in timeout={500}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 5,
          border: "1px solid",
          borderColor: isDelayed ? "error.main" : "#e2e8f0",
          bgcolor: isDelayed ? "rgba(254, 242, 242, 0.4)" : "#fff",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          overflow: "visible",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 15px 30px -10px rgba(0,0,0,0.12)",
            borderColor: "primary.light"
          },
          ...(isDelayed && {
            animation: "pulse_normal 2s infinite ease-in-out",
            "@keyframes pulse_normal": {
              "0%": { boxShadow: "0 0 0 0 rgba(239, 68, 68, 0.4)" },
              "70%": { boxShadow: "0 0 0 12px rgba(239, 68, 68, 0)" },
              "100%": { boxShadow: "0 0 0 0 rgba(239, 68, 68, 0)" }
            }
          })
        }}
      >
        {/* INDICADOR DE DEMORA */}
        {isDelayed && (
          <Box
            sx={{
              position: "absolute",
              bottom: -12,
              left: compact ? 12 : 24,
              bgcolor: "error.main",
              color: "#fff",
              borderRadius: "20px",
              px: 2,
              py: 0.5,
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              gap: 0.8,
              boxShadow: 3
            }}
          >
            <NotificationsActive sx={{ fontSize: 16 }} />
            <Typography variant="caption" fontWeight={900}>DEMORADA: {minutes} MIN</Typography>
          </Box>
        )}

        <CardContent sx={{ p: compact ? 0.75 : 1.25, pb: "10px !important", flexGrow: 1 }}>
          {/* CABECERA */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
            <Box sx={{ width: "100%", pr: 2 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ mb: compact ? 0 : 0.5 }}>
                <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={1}>
                  <Typography variant={compact ? "subtitle2" : "h6"} fontWeight={850} color="text.primary" sx={{ lineHeight: 1.1 }}>
                    {(() => {
                      const custName = (order as any).customerName || order.name;
                      if (order.orderType === 'mesa') {
                        const table = order.tableNumber || (order.name && order.name.toLowerCase().includes('mesa') ? order.name : '');
                        return table ? (table.toLowerCase().includes('mesa') ? table : `Mesa: ${table}`) : (custName || 'Mesa sin número');
                      }
                      if (order.orderType === 'llevar') {
                        return custName ? `Recoge: ${custName}` : (order.name && order.name !== 'Cliente' ? order.name : 'Recoger en tienda');
                      }
                      if (order.orderType === 'domicilio') {
                        return custName ? `Cliente: ${custName}` : (order.address ? `Dir: ${order.address}` : (order.name && order.name !== 'Cliente' ? order.name : 'Domicilio'));
                      }
                      return (custName && custName !== 'Cliente') ? custName : (order.tableNumber || order.address || (order.name || 'Pedido'));
                    })()}
                  </Typography>
                </Stack>

                {compact && (
                  <Typography variant="caption" color={isDelayed ? "error.main" : "text.secondary"} fontWeight={800}>
                    {minutes}m
                  </Typography>
                )}
              </Stack>

              {!compact && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                  {order.createdByName ? `Realizado por: ${order.createdByName} (${order.createdByRole})` : 'Creado por: Cliente (App/Web)'}
                </Typography>
              )}
            </Box>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2" fontWeight={900} color="text.secondary" sx={{ bgcolor: '#f1f5f9', px: 1, py: 0.2, borderRadius: 1.5 }}>
                #{order.supportCode || (order.id ? order.id.slice(-4).toUpperCase() : "N/A")}
              </Typography>

              {!compact && (
                <Chip
                  icon={statusConfig[order.state]?.icon}
                  label={statusConfig[order.state]?.label ?? "Estado"}
                  color={statusConfig[order.state]?.color}
                  size="small"
                  sx={{
                    fontWeight: 800,
                    borderRadius: 2,
                    px: 0.5,
                    "& .MuiChip-label": { fontSize: "0.75rem" }
                  }}
                />
              )}
            </Stack>
          </Stack>



          {/* INFORMACIÓN DEL CLIENTE */}
          {!compact && (() => {
            const clientPhone = (order as any).phoneNumber || (order as any).phone || (order as any).customerPhone || (order as any).clientPhone;
            const isDomicilio = !order.orderType || order.orderType === 'domicilio';
            const isLlevar = order.orderType === 'llevar';
            const isMesa = order.orderType === 'mesa';
            const clientAddress = order.address || (isDomicilio ? order.tableNumber : undefined);
            const clientNeighborhood = (order as any).neighborhoodName || (order as any).neighborhood;
            const clientName = (order as any).customerName || (order.name !== 'Cliente' ? order.name : undefined);

            if (!clientPhone && !clientAddress && !clientNeighborhood && !clientName && !order.tableNumber) return null;

            return (
              <Stack direction="column" spacing={0.25} mb={0.75} sx={{ bgcolor: "#f8fafc", p: 0.75, borderRadius: 1.5 }}>

                {/* Datos */}
                <Stack direction="column" spacing={0.25}>
                  {clientName && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Person color="action" sx={{ fontSize: 16 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={700} noWrap>
                        {isLlevar ? `Quién recoge: ${clientName}` : `Cliente: ${clientName}`}
                      </Typography>
                    </Stack>
                  )}
                  {clientPhone && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Phone color="action" sx={{ fontSize: 16 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={700} noWrap>
                        Tel: {clientPhone}
                      </Typography>
                    </Stack>
                  )}
                  {isDomicilio && (clientAddress || clientNeighborhood) && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <DeliveryDining color="action" sx={{ fontSize: 16 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={700} noWrap>
                        Dir: {clientAddress || 'No especificada'} {clientNeighborhood ? `- ${clientNeighborhood}` : ''}
                      </Typography>
                    </Stack>
                  )}
                  {isMesa && order.tableNumber && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TableBar color="action" sx={{ fontSize: 16 }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={700} noWrap>
                        {order.tableNumber.toLowerCase().includes('mesa') ? order.tableNumber : `Mesa: ${order.tableNumber}`}
                      </Typography>
                    </Stack>
                  )}
                </Stack>

                {/* Botones de Acción Rápida */}
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5, mt: 0.25 }}>
                  {clientPhone && (userRole === 'domiciliario' || userRole === 'admin') && (
                    <>
                      <a
                        href={`https://wa.me/${clientPhone.replace(/\D/g, '')}?text=${encodeURIComponent("Hola, nos comunicamos de Thomas Pizza.")}`}
                        target="_blank" rel="noopener noreferrer"
                        style={{
                          display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 8px',
                          backgroundColor: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.02)', textDecoration: 'none',
                          color: '#475569', fontSize: '0.7rem', fontWeight: 600, transition: 'all 0.2s ease'
                        }}
                      >
                        <img src="https://img.icons8.com/color/48/whatsapp--v1.png" alt="WhatsApp" style={{ width: '14px', height: '14px' }} />
                        WhatsApp
                      </a>
                      <a
                        href={`tel:${clientPhone.replace(/\D/g, '')}`}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 8px',
                          backgroundColor: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.02)', textDecoration: 'none',
                          color: '#475569', fontSize: '0.7rem', fontWeight: 600, transition: 'all 0.2s ease'
                        }}
                      >
                        <img src="https://img.icons8.com/color/48/phone.png" alt="Llamar" style={{ width: '14px', height: '14px' }} />
                        Llamar
                      </a>
                    </>
                  )}
                  {clientAddress && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${clientAddress} ${clientNeighborhood || ''}`)}`}
                      target="_blank" rel="noopener noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 8px',
                        backgroundColor: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.02)', textDecoration: 'none',
                        color: '#475569', fontSize: '0.7rem', fontWeight: 600, transition: 'all 0.2s ease'
                      }}
                    >
                      <img src="https://img.icons8.com/color/48/google-maps-new.png" alt="Maps" style={{ width: '14px', height: '14px' }} />
                      Maps
                    </a>
                  )}
                  {clientAddress && (
                    <a
                      href={`https://waze.com/ul?q=${encodeURIComponent(`${clientAddress} ${clientNeighborhood || ''}`)}`}
                      target="_blank" rel="noopener noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 8px',
                        backgroundColor: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.02)', textDecoration: 'none',
                        color: '#475569', fontSize: '0.7rem', fontWeight: 600, transition: 'all 0.2s ease'
                      }}
                    >
                      <img src="https://img.icons8.com/color/48/waze.png" alt="Waze" style={{ width: '14px', height: '14px' }} />
                      Waze
                    </a>
                  )}
                </Stack>
              </Stack>
            );
          })()}



          {/* NOTA */}
          {order.notes && (
            <Box
              mb={0.75}
              p={0.75}
              sx={{
                bgcolor: "#fffbeb",
                borderRadius: 1.5,
                borderLeft: "4px solid #f59e0b"
              }}
            >
              <Typography variant="caption" color="#92400e" fontWeight={1000} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.2, fontSize: "0.65rem" }}>
                <InfoOutlined sx={{ fontSize: 14 }} /> {compact ? "NOTA" : "NOTA CLIENTE"}
              </Typography>
              <Typography variant="caption" color="#b45309" fontWeight={500} sx={{ fontStyle: 'italic', display: 'block' }}>
                "{order.notes}"
              </Typography>
            </Box>
          )}

          {/* COMPROBANTE DE PAGO */}
          {!compact && order.receiptUrl && (
            <Box mb={1.5}>
              <Button
                variant="outlined"
                color="info"
                startIcon={<Receipt />}
                href={order.receiptUrl}
                target="_blank"
                fullWidth
                sx={{ borderRadius: 2, fontWeight: 700 }}
              >
                Ver Comprobante
              </Button>
            </Box>
          )}

          {!compact && <Divider sx={{ mb: 1.5, borderStyle: "dashed" }} />}

          {/* PRODUCTOS RENDERIZADOS UNIFICADOS */}
          {(() => {
            const visibleProducts = [...foodItems, ...(userRole !== "cocinero" ? drinkItems : [])];
            if (visibleProducts.length === 0) return null;
            return (
              <Stack spacing={compact ? 0.5 : 1} mb={compact ? 0 : 1.5}>
                {visibleProducts.map((p) => {
                  const idx = order.products.indexOf(p);
                  return renderProduct(p, idx);
                })}
              </Stack>
            );
          })()}

          {/* RESUMEN DE MÉTRICAS EN TIEMPO REAL */}
          {!compact && showTiempos && (
            <Box mt={1} p={1} bgcolor="grey.50" borderRadius={1.5} border="1px dashed" borderColor="grey.300">
              <Typography variant="caption" fontWeight={900} color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: "0.65rem" }}>
                TIEMPOS POR ETAPA
              </Typography>
              <Stack spacing={1}>
                {order.stageMetrics && Object.entries(order.stageMetrics)
                  .sort(([stageA], [stageB]) => (STAGE_ORDER[stageA] || 99) - (STAGE_ORDER[stageB] || 99))
                  .map(([stage, metric]) => (
                    <Stack key={stage} direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" fontWeight={800} color="text.primary" sx={{ textTransform: 'capitalize' }}>
                        {stage.replace(/_/g, ' ')}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {metric.userName} {metric.userRole ? `(${metric.userRole})` : ''}
                        </Typography>
                        <Chip
                          label={`${metric.durationMinutes || 0} min`}
                          size="small"
                          color={(metric.durationMinutes || 0) > 15 ? "error" : "success"}
                          variant="outlined"
                          sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }}
                        />
                      </Stack>
                    </Stack>
                  ))}

                {/* ETAPA ACTUAL (EN TIEMPO REAL) */}
                {!["entregado", "completada", "cancelada"].includes(order.state) && (
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="caption" fontWeight={800} color="primary.main" sx={{ textTransform: 'capitalize' }}>
                        {!order.preparadorId && ["enPreparacion", "enMasa", "pendiente", "enProceso"].includes(order.state)
                          ? "enMasa"
                          : order.state.replace(/_/g, ' ')}
                      </Typography>
                      <CircularProgress size={10} thickness={6} sx={{ color: 'primary.main' }} />
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {!order.preparadorId && ["enPreparacion", "enMasa", "pendiente", "enProceso"].includes(order.state)
                          ? (order.masaUserName || userName || "Masa")
                          : ["enPreparacion", "enMasa", "enEmpaque"].includes(order.state) && order.preparadorName
                            ? order.preparadorName
                            : (order.state === "enCamino" || order.state === "esperando_domiciliario") && (order as any).deliveryDriverName
                              ? (order as any).deliveryDriverName
                              : "En curso..."}
                      </Typography>
                      <Chip
                        label={`${stateMinutes} min`}
                        size="small"
                        color={stateMinutes > 15 ? "error" : "warning"}
                        sx={{ height: 18, fontSize: "0.65rem", fontWeight: 900, animation: 'pulse 2s infinite' }}
                      />
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </Box>
          )}
        </CardContent>

        {/* ACCIONES */}
        <CardActions sx={{ p: compact ? 0.75 : 1.25, pt: 0, flexDirection: "column", gap: 0.75 }}>

          {!compact && (
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" width="100%" px={0.5} mb={0.25}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {order.orderType && (
                  <Chip
                    icon={
                      order.orderType === 'domicilio' ? <DeliveryDining fontSize="small" /> :
                        order.orderType === 'llevar' ? <TakeoutDining fontSize="small" /> :
                          <Restaurant fontSize="small" />
                    }
                    label={
                      order.orderType === 'domicilio' ? 'Domicilio' :
                        order.orderType === 'llevar' ? 'Recoger en tienda' :
                          'Comer Acá'
                    }
                    color={
                      order.orderType === 'domicilio' ? 'info' :
                        order.orderType === 'llevar' ? 'warning' :
                          'primary'
                    }
                    size="small"
                    sx={{ height: 22, fontSize: '0.7rem', fontWeight: 800, "& .MuiChip-icon": { fontSize: 14 } }}
                  />
                )}
                {order.paymentMethod && (
                  <Chip
                    icon={
                      order.paymentMethod.toLowerCase() === 'efectivo'
                        ? <LocalAtm fontSize="small" />
                        : order.paymentMethod.toLowerCase() === 'tarjeta'
                        ? <CreditCard fontSize="small" />
                        : <Payments fontSize="small" />
                    }
                    label={order.paymentMethod.toUpperCase()}
                    color={
                      order.paymentMethod.toLowerCase() === 'efectivo'
                        ? 'success'
                        : order.paymentMethod.toLowerCase() === 'tarjeta'
                        ? 'info'
                        : 'secondary'
                    }
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.7rem', fontWeight: 800, "& .MuiChip-icon": { fontSize: 14 } }}
                  />
                )}
                <Chip
                  icon={isPaidOrder ? <CheckCircle sx={{ fontSize: "12px !important" }} /> : <AccessTime sx={{ fontSize: "12px !important" }} />}
                  label={isPaidOrder ? "Pagado" : "Pendiente"}
                  color={isPaidOrder ? "success" : "warning"}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 19,
                    fontSize: '0.65rem',
                    fontWeight: 800,
                    borderRadius: 1.5,
                    "& .MuiChip-label": { px: 0.6, py: 0 }
                  }}
                />
              </Stack>
              <Box textAlign="right">
                <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ letterSpacing: 0.5, display: "block", mb: -0.5 }}>
                  TOTAL ORDEN
                </Typography>
                <Typography variant="h6" color="primary.main" fontWeight={900}>
                  ${totalGeneral.toLocaleString()}
                </Typography>
              </Box>
            </Stack>
          )}

          {!hideActions && (
            <Stack direction="row" spacing={1.5} width="100%" alignItems="center" justifyContent="space-between">
              {/* ACCIONES SECUNDARIAS (Anular, Editar, Eliminar) */}
              <Stack direction="row" spacing={0.75} alignItems="center">
                {["verificando_pago", "pendiente", "enProceso", "enMasa"].includes(order.state) && (
                  <MuiTooltip title="Anular Orden" arrow>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={handleCancelOrder}
                      sx={{
                        borderRadius: 2,
                        p: 0.8,
                        border: "1px solid",
                        borderColor: "error.light",
                        flexShrink: 0
                      }}
                    >
                      <Block fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                )}

                {/* Solo mostramos Editar y Eliminar si NO está en la base de Masa (donde nextState es enPreparacion) ni en empaque/domicilios */}
                {!compact && nextStateInfo?.nextState !== "enPreparacion" && !["enEmpaque", "esperando_domiciliario", "enCamino"].includes(order.state) && (
                  <>
                    <MuiTooltip title="Agregar Productos / Editar" arrow>
                      <IconButton
                        color="primary"
                        onClick={() => onEdit(order.id!)}
                        sx={{
                          borderRadius: 2,
                          p: 0.8,
                          border: "1px solid",
                          borderColor: "primary.light"
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </MuiTooltip>

                    {canDelete && (
                      <MuiTooltip title="Eliminar" arrow>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => onDelete(order.id!)}
                          sx={{
                            borderRadius: 2,
                            p: 0.8,
                            border: "1px solid",
                            borderColor: "error.light"
                          }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </MuiTooltip>
                    )}
                  </>
                )}

                {onPrintComanda && (
                  <MuiTooltip title="Imprimir Comanda" arrow>
                    <IconButton
                      color="secondary"
                      size="small"
                      onClick={() => onPrintComanda(order)}
                      sx={{
                        borderRadius: 2,
                        p: 0.8,
                        border: "1px solid",
                        borderColor: "secondary.light"
                      }}
                    >
                      <PrintIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                )}
              </Stack>

              {/* BOTÓN PRINCIPAL / PASAR DE FASE */}
              {["pendiente", "enProceso", "enMasa", "enPreparacion"].includes(order.state) && !order.preparadorId && prepUsers && onAssignPreparador ? (
                <Select
                  displayEmpty
                  value=""
                  onChange={(e) => {
                    const uid = e.target.value as string;
                    const u = prepUsers.find(x => x.uid === uid || (x as any).id === uid);
                    if (u) {
                      onAssignPreparador(order.id!, uid, u.name || "Preparador");
                    }
                  }}
                  fullWidth
                  size="small"
                  sx={{ borderRadius: 2.5, fontWeight: 800, "& .MuiSelect-select": { py: 1.5 }, bgcolor: "primary.50", color: "primary.main" }}
                >
                  <MenuItem disabled value="">
                    <em>Asignar Preparador...</em>
                  </MenuItem>
                  {prepUsers.map(u => (
                    <MenuItem key={u.uid || (u as any).id} value={u.uid || (u as any).id}>
                      {u.name}
                    </MenuItem>
                  ))}
                </Select>
              ) : order.state === "esperando_domiciliario" && !(order as any).deliveryDriverName && domiUsers && onAssignDomiciliario ? (
                <Select
                  displayEmpty
                  value=""
                  onChange={(e) => {
                    const uid = e.target.value as string;
                    const u = domiUsers.find(x => x.uid === uid || (x as any).id === uid);
                    if (u) {
                      onAssignDomiciliario(order.id!, uid, u.name || "Domiciliario");
                    }
                  }}
                  fullWidth
                  size="small"
                  sx={{ borderRadius: 2.5, fontWeight: 800, "& .MuiSelect-select": { py: 1.5 }, bgcolor: "info.50", color: "info.main" }}
                >
                  <MenuItem disabled value="">
                    <em>Asignar Domiciliario...</em>
                  </MenuItem>
                  {domiUsers.map(u => (
                    <MenuItem key={u.uid || (u as any).id} value={u.uid || (u as any).id}>
                      {u.name}
                    </MenuItem>
                  ))}
                </Select>
              ) : nextStateInfo && (
                compact || (["enPreparacion", "enMasa", "pendiente", "enProceso"].includes(order.state) && !order.preparadorId) ? null : (
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    onClick={() => {
                      if (nextStateInfo.nextState === "listo / caja" || nextStateInfo.nextState === "entregado") {
                        if (nextStateInfo.nextState === "entregado") {
                          if (onAutoCheckout) {
                            onAutoCheckout(order);
                          } else {
                            onUpdateStatus(order, "entregado");
                          }
                        } else {
                          onComplete(order);
                        }
                      } else {
                        onUpdateStatus(order, nextStateInfo.nextState);
                      }
                    }}
                    endIcon={<KeyboardArrowRight />}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      fontSize: "0.9rem",
                      fontWeight: 800,
                      textTransform: "none",
                      boxShadow: "0 2px 8px rgba(16, 185, 129, 0.2)"
                    }}
                  >
                    {nextStateInfo.label}
                  </Button>
                )
              )}
            </Stack>
          )}
        </CardActions>
      </Card>
    </Fade>
  );
}
